import React, { FC, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfiguration } from '../../context/ConfigurationContext';
import type { Contact } from '../../context/ConfigurationContext';
import { ContactPhotoPicker } from '../../components/features/contactPhoto/ContactPhotoPicker';
import SexAtBirthPopover from '../../components/features/SexAtBirthPopover';
import SexAtBirthModal from '../../components/features/SexAtBirthModal';

interface ContactSetupProps {
  onNext: () => void;
  onBack: () => void;
}

const RELATIONSHIP_OPTIONS_EN = [
  { value: 'son', label: 'Son' },
  { value: 'daughter', label: 'Daughter' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'grandchild', label: 'Grandchild' },
  { value: 'friend', label: 'Friend' },
  { value: 'neighbor', label: 'Neighbor' },
  { value: 'doctor', label: 'Doctor' },
  { value: 'caregiver', label: 'Caregiver' },
  { value: 'pharmacy', label: 'Pharmacy' },
  { value: 'other', label: 'Other' },
];

const RELATIONSHIP_OPTIONS_FR = [
  { value: 'son', label: 'Fils' },
  { value: 'daughter', label: 'Fille' },
  { value: 'spouse', label: 'Conjoint(e)' },
  { value: 'sibling', label: 'Fr\u00e8re/S\u0153ur' },
  { value: 'grandchild', label: 'Petit-enfant' },
  { value: 'friend', label: 'Ami(e)' },
  { value: 'neighbor', label: 'Voisin(e)' },
  { value: 'doctor', label: 'M\u00e9decin' },
  { value: 'caregiver', label: 'Aidant(e)' },
  { value: 'pharmacy', label: 'Pharmacie' },
  { value: 'other', label: 'Autre' },
];

const ContactSetup: FC<ContactSetupProps> = ({ onNext, onBack }) => {
  const { i18n } = useTranslation();
  const isFr = i18n.language?.startsWith('fr');
  const RELATIONSHIP_OPTIONS = isFr ? RELATIONSHIP_OPTIONS_FR : RELATIONSHIP_OPTIONS_EN;

  const {
    contacts,
    addContact,
    removeContact,
    updateContact,
    userDetails,
    setUserDetails,
  } = useConfiguration();

  const [isAddingContact, setIsAddingContact] = useState(false);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [seniorName, setSeniorName] = useState(userDetails.fullName || '');
  const [sexAtBirth, setSexAtBirth] = useState<'male' | 'female' | null>(userDetails.sexAtBirth || null);
  const [sexPopoverOpen, setSexPopoverOpen] = useState(false);
  const [sexModalOpen, setSexModalOpen] = useState(false);
  const [sexSkipped, setSexSkipped] = useState(false);
  const popoverCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPopoverClose = () => {
    if (popoverCloseTimerRef.current) {
      clearTimeout(popoverCloseTimerRef.current);
      popoverCloseTimerRef.current = null;
    }
  };

  const schedulePopoverClose = () => {
    cancelPopoverClose();
    popoverCloseTimerRef.current = setTimeout(() => {
      setSexPopoverOpen(false);
    }, 250);
  };

  const openPopoverFromHover = () => {
    cancelPopoverClose();
    setSexPopoverOpen(true);
  };

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    relationship: 'son',
    isEmergency: false,
    photoUrl: '',
    aliases: '',
  });

  // Contact photos at sign-up stay on this device (ContactPhotoPicker
  // keepOnDevice): she has no account yet, and nobody may upload before
  // signing in. Deployment uploads them right after the account is created,
  // so nothing is ever stored — or needs deleting — from this step.
  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      relationship: 'son',
      isEmergency: false,
      photoUrl: '',
      aliases: '',
    });
    setIsAddingContact(false);
    setEditingContactId(null);
  };

  const handleFormPhoto = (url: string | null) => {
    setFormData(prev => ({ ...prev, photoUrl: url || '' }));
  };

  const handleListPhoto = (contact: Contact, url: string | null) => {
    updateContact(contact.id, { photoUrl: url || '' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.phone.trim()) {
      return;
    }

    const parsedAliases = formData.aliases
      .split(',')
      .map(a => a.trim())
      .filter(a => a.length > 0);

    if (editingContactId) {
      updateContact(editingContactId, {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        relationship: formData.relationship,
        isEmergency: formData.isEmergency,
        photoUrl: formData.photoUrl,
        aliases: parsedAliases,
      });
    } else {
      addContact({
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        relationship: formData.relationship,
        isEmergency: formData.isEmergency,
        photoUrl: formData.photoUrl,
        aliases: parsedAliases,
      });
    }

    resetForm();
  };

  const handleEdit = (contact: Contact) => {
    setFormData({
      name: contact.name,
      phone: contact.phone,
      relationship: contact.relationship,
      isEmergency: contact.isEmergency,
      photoUrl: contact.photoUrl || '',
      aliases: (contact.aliases || []).join(', '),
    });
    setEditingContactId(contact.id);
    setIsAddingContact(true);
  };

  const handleSeniorNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setSeniorName(newName);
    setUserDetails({ ...userDetails, fullName: newName });
  };

  const handleSexAtBirthChange = (value: 'male' | 'female') => {
    setSexAtBirth(value);
    setSexSkipped(false);
    setUserDetails({ ...userDetails, sexAtBirth: value });
  };

  const handleIllAnswer = () => {
    setSexPopoverOpen(false);
    setSexSkipped(false);
  };

  const handleSkipAnyway = () => {
    setSexPopoverOpen(false);
    setSexSkipped(true);
    setSexAtBirth(null);
    setUserDetails({ ...userDetails, sexAtBirth: null });
  };

  const handleContinue = () => {
    if (contacts.length >= 1 && seniorName.trim() && (sexAtBirth !== null || sexSkipped)) {
      onNext();
    }
  };

  const canContinue = contacts.length >= 1 && seniorName.trim().length > 0 && (sexAtBirth !== null || sexSkipped);

  return (
    <div className="min-h-screen bg-brand-bg dark:bg-slate-900 font-body pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-brand-border dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-brand-text-light hover:text-brand-text dark:text-slate-400 dark:hover:text-white transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="font-medium">{isFr ? 'Retour' : 'Back'}</span>
            </button>

            <div className="flex items-center gap-2">
              <span className="font-bold text-brand-text dark:text-white">{isFr ? 'Ajouter des contacts' : 'Add Contacts'}</span>
            </div>

            <div className="text-sm text-brand-text-muted">
              {isFr ? '\u00c9tape 4 de 7' : 'Step 4 of 7'}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-1.5 bg-brand-bg-warm dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-brand-primary rounded-full transition-all duration-500" style={{ width: '57%' }} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-brand-text dark:text-white mb-4">
            {isFr ? 'Configurez le t\u00e9l\u00e9phone de votre proche' : "Set up your loved one's phone"}
          </h1>
          <p className="text-brand-text-light dark:text-slate-400">
            {isFr ? 'Entrez son nom et ajoutez les contacts importants qui appara\u00eetront sur son t\u00e9l\u00e9phone.' : 'Enter their name and add important contacts that will appear on their phone.'}
          </p>
        </div>

        {/* Senior Name Input */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 sm:p-6 border border-brand-border dark:border-slate-700 mb-6 sm:mb-8">
          <label className="block text-sm font-bold text-brand-text-light dark:text-slate-300 mb-2">
            {isFr ? 'Nom du proche' : "Senior's Name"} *
          </label>
          <input
            type="text"
            value={seniorName}
            onChange={handleSeniorNameChange}
            placeholder="e.g., Mom, Grandpa Joe, Margaret"
            className="w-full px-4 py-3 rounded-xl border-2 border-brand-border dark:border-slate-600 bg-white dark:bg-slate-700 text-brand-text dark:text-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all text-lg"
          />
          <p className="text-sm text-brand-text-muted mt-2">
            {isFr ? 'Ce nom sera utilis\u00e9 pour identifier votre proche dans le tableau de bord' : 'This name will be used to identify this senior in your dashboard'}
          </p>

          {/* Sex at Birth */}
          <div className="mt-5 pt-5 border-t border-brand-border dark:border-slate-700">
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <label className="block text-sm font-bold text-brand-text-light dark:text-slate-300">
                  {isFr ? 'Sexe à la naissance' : 'Sex at birth'} *
                </label>
                <button
                  onClick={() => setSexPopoverOpen(!sexPopoverOpen)}
                  className="p-0 bg-transparent border-none cursor-pointer text-brand-text-muted hover:text-brand-primary transition-colors"
                  aria-label={isFr ? 'Pourquoi posons-nous cette question?' : 'Why we ask this question'}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>info</span>
                </button>
              </div>

              {sexPopoverOpen && (
                <SexAtBirthPopover
                  isFr={isFr}
                  onClose={() => setSexPopoverOpen(false)}
                  onLearnMore={() => { setSexPopoverOpen(false); setSexModalOpen(true); }}
                  onIllAnswer={handleIllAnswer}
                  onSkipAnyway={handleSkipAnyway}
                  onMouseEnter={cancelPopoverClose}
                  onMouseLeave={schedulePopoverClose}
                />
              )}

              <div className="space-y-3">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handleSexAtBirthChange('male')}
                    className={`flex-1 px-4 py-3 rounded-xl border-2 font-medium text-base transition-all ${
                      sexAtBirth === 'male'
                        ? 'border-brand-primary bg-brand-primary/5 text-brand-primary'
                        : 'border-brand-border dark:border-slate-600 text-brand-text-light dark:text-slate-300 hover:border-brand-primary/50'
                    }`}
                  >
                    {isFr ? 'Masculin' : 'Male'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSexAtBirthChange('female')}
                    className={`flex-1 px-4 py-3 rounded-xl border-2 font-medium text-base transition-all ${
                      sexAtBirth === 'female'
                        ? 'border-brand-primary bg-brand-primary/5 text-brand-primary'
                        : 'border-brand-border dark:border-slate-600 text-brand-text-light dark:text-slate-300 hover:border-brand-primary/50'
                    }`}
                  >
                    {isFr ? 'Féminin' : 'Female'}
                  </button>
                </div>
                <button
                  type="button"
                  onMouseEnter={openPopoverFromHover}
                  onMouseLeave={schedulePopoverClose}
                  onTouchStart={openPopoverFromHover}
                  onClick={() => {/* selection handled via popover buttons */}}
                  className={`w-full px-4 py-3 rounded-xl border-2 font-medium text-base transition-all ${
                    sexSkipped
                      ? 'border-brand-primary bg-brand-primary/5 text-brand-primary'
                      : 'border-brand-border dark:border-slate-600 text-brand-text-light dark:text-slate-300 hover:border-brand-primary/50'
                  }`}
                >
                  {isFr ? 'Je préfère ne pas répondre' : "I'd rather skip this"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {sexModalOpen && (
          <SexAtBirthModal isFr={isFr} onClose={() => setSexModalOpen(false)} />
        )}

        {/* Contacts Section Header */}
        <div className="mb-6">
          <h2 className="text-xl font-display font-bold text-brand-text dark:text-white mb-2">
            {isFr ? 'Contacts importants' : 'Important Contacts'}
          </h2>
          <p className="text-brand-text-light dark:text-slate-400 text-sm">
            {isFr ? 'Ajoutez au moins un contact pour continuer' : 'Add at least one contact to continue'}
          </p>
        </div>

        {/* Contact List */}
        {contacts.length > 0 && !isAddingContact && (
          <div className="space-y-4 mb-8">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className="flex items-center gap-3 sm:gap-4 p-4 bg-white dark:bg-slate-800 rounded-2xl border border-brand-border dark:border-slate-700"
              >
                {/* Photo — tap to add, change or remove */}
                <ContactPhotoPicker
                  photoUrl={contact.photoUrl}
                  contactName={contact.name}
                  onChange={(url) => handleListPhoto(contact, url)}
                  thumbnailWidth={72}
                  keepOnDevice
                />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-brand-text dark:text-white truncate">
                      {contact.name}
                    </h3>
                    {contact.isEmergency && (
                      <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-bold rounded-full">
                        {isFr ? 'Urgence' : 'Emergency'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-brand-text-muted dark:text-slate-400 truncate">
                    {contact.phone} • {RELATIONSHIP_OPTIONS.find(r => r.value === contact.relationship)?.label}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(contact)}
                    className="p-2 text-brand-text-muted hover:text-brand-primary transition-colors"
                  >
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                  <button
                    onClick={() => removeContact(contact.id)}
                    className="p-2 text-brand-text-muted hover:text-red-500 transition-colors"
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Contact Form */}
        {isAddingContact ? (
          <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-brand-border dark:border-slate-700">
            <h3 className="text-xl font-display font-bold text-brand-text dark:text-white mb-6">
              {editingContactId ? (isFr ? 'Modifier le contact' : 'Edit Contact') : (isFr ? 'Ajouter un contact' : 'Add New Contact')}
            </h3>

            {/* Photo: always through the crop step, kept on this device until sign-up ends */}
            <div className="flex flex-col items-center mb-6">
              <ContactPhotoPicker
                photoUrl={formData.photoUrl || undefined}
                contactName={formData.name}
                onChange={handleFormPhoto}
                thumbnailWidth={120}
                keepOnDevice
              />
              <p className="text-sm text-brand-text-muted mt-2">
                {formData.photoUrl ? (isFr ? 'Cliquez pour changer la photo' : 'Click to change photo') : (isFr ? 'Ajouter une photo (optionnel)' : 'Add a photo (optional)')}
              </p>
            </div>

            {/* Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text-light dark:text-slate-300 mb-2">
                {isFr ? 'Nom' : 'Name'} *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Mom, John Smith"
                className="w-full px-4 py-3 rounded-xl border-2 border-brand-border dark:border-slate-600 bg-white dark:bg-slate-700 text-brand-text dark:text-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
                required
              />
            </div>

            {/* Phone */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text-light dark:text-slate-300 mb-2">
                {isFr ? 'Num\u00e9ro de t\u00e9l\u00e9phone' : 'Phone Number'} *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="e.g., (555) 123-4567"
                className="w-full px-4 py-3 rounded-xl border-2 border-brand-border dark:border-slate-600 bg-white dark:bg-slate-700 text-brand-text dark:text-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
                required
              />
            </div>

            {/* Relationship */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text-light dark:text-slate-300 mb-2">
                {isFr ? 'Lien de parent\u00e9' : 'Relationship'}
              </label>
              <select
                value={formData.relationship}
                onChange={(e) => setFormData(prev => ({ ...prev, relationship: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border-2 border-brand-border dark:border-slate-600 bg-white dark:bg-slate-700 text-brand-text dark:text-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
              >
                {RELATIONSHIP_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Nicknames / Aliases */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text-light dark:text-slate-300 mb-2">
                {isFr ? 'Surnoms (optionnel)' : 'Nicknames (optional)'}
              </label>
              <input
                type="text"
                value={formData.aliases}
                onChange={(e) => setFormData(prev => ({ ...prev, aliases: e.target.value }))}
                placeholder="e.g., Tom, Tommy, Papa"
                className="w-full px-4 py-3 rounded-xl border-2 border-brand-border dark:border-slate-600 bg-white dark:bg-slate-700 text-brand-text dark:text-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
              />
              <p className="text-sm text-brand-text-muted mt-1">
                {isFr ? 'Surnoms s\u00e9par\u00e9s par des virgules que le proche utilise pour cette personne' : 'Comma-separated nicknames the senior uses for this person'}
              </p>
            </div>

            {/* Emergency Contact Toggle */}
            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isEmergency}
                  onChange={(e) => setFormData(prev => ({ ...prev, isEmergency: e.target.checked }))}
                  className="w-5 h-5 rounded border-brand-border text-brand-primary focus:ring-brand-primary/20"
                />
                <div>
                  <span className="font-medium text-brand-text dark:text-white">{isFr ? "Contact d'urgence" : 'Emergency Contact'}</span>
                  <p className="text-sm text-brand-text-muted">{isFr ? 'Cette personne sera notifi\u00e9e en cas d\u2019urgence' : 'This person will be notified in emergencies'}</p>
                </div>
              </label>
            </div>

            {/* Form Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 px-6 py-3 border-2 border-brand-border dark:border-slate-600 text-brand-text-light dark:text-slate-300 font-medium rounded-xl hover:bg-brand-bg-warm dark:hover:bg-slate-700 transition-colors"
              >
                {isFr ? 'Annuler' : 'Cancel'}
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-brand-accent text-white font-bold rounded-xl hover:bg-[#c96a3f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingContactId ? (isFr ? 'Enregistrer les modifications' : 'Save Changes') : (isFr ? 'Ajouter le contact' : 'Add Contact')}
              </button>
            </div>
          </form>
        ) : (
          /* Add Contact Button */
          <button
            onClick={() => setIsAddingContact(true)}
            className="w-full p-6 border-2 border-dashed border-brand-border dark:border-slate-600 rounded-2xl hover:border-brand-primary hover:bg-brand-primary-light transition-all group"
          >
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-brand-bg-warm dark:bg-slate-700 flex items-center justify-center group-hover:bg-brand-primary-light transition-colors">
                <span className="material-symbols-outlined text-3xl text-brand-text-muted group-hover:text-brand-primary transition-colors">
                  person_add
                </span>
              </div>
              <span className="font-medium text-brand-text-light dark:text-slate-400 group-hover:text-brand-primary transition-colors">
                {isFr ? 'Ajouter un contact' : 'Add a Contact'}
              </span>
            </div>
          </button>
        )}

        {/* Helper Text */}
        {!canContinue && !isAddingContact && (
          <p className="text-center text-sm text-brand-text-muted mt-6">
            {(!seniorName.trim() || (!sexAtBirth && !sexSkipped)) && contacts.length === 0
              ? (isFr ? 'Remplissez les informations du proche et ajoutez au moins un contact' : 'Fill in senior info and add at least one contact to continue')
              : !seniorName.trim() || (!sexAtBirth && !sexSkipped)
              ? (isFr ? 'Remplissez les informations du proche pour continuer' : 'Complete senior info to continue')
              : (isFr ? 'Ajoutez au moins un contact pour continuer' : 'Add at least one contact to continue')}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-brand-border dark:border-slate-800 p-4">
        <div className="max-w-3xl mx-auto flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
          <button
            onClick={onBack}
            className="w-full sm:w-auto px-6 py-3 text-brand-text-light dark:text-slate-400 font-medium hover:text-brand-text dark:hover:text-white transition-colors"
          >
            {isFr ? 'Retour' : 'Back'}
          </button>

          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full sm:w-auto">
            {contacts.length > 0 && (
              <p className="text-sm text-brand-text-muted">
                {isFr ? `${contacts.length} contact${contacts.length !== 1 ? 's' : ''} ajout\u00e9${contacts.length !== 1 ? 's' : ''}` : `${contacts.length} contact${contacts.length !== 1 ? 's' : ''} added`}
              </p>
            )}
            {canContinue ? (
              <button
                onClick={handleContinue}
                className="w-full sm:w-auto px-8 py-3 bg-brand-accent text-white font-bold rounded-xl hover:bg-[#c96a3f] transition-all shadow-lg shadow-brand-accent/30"
              >
                {isFr ? 'Continuer vers les fonctions' : 'Continue to Features'}
                <span className="material-symbols-outlined ml-2 align-middle">arrow_forward</span>
              </button>
            ) : (
              <button
                disabled
                className="w-full sm:w-auto px-8 py-3 bg-brand-bg-warm dark:bg-slate-700 text-brand-text-muted font-bold rounded-xl cursor-not-allowed"
              >
                {isFr ? 'Remplissez les champs requis' : 'Complete required fields'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSetup;
